import React, { useEffect, useState } from 'react';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { PeriodListPicker } from './PeriodListPicker';
import { PrimaryButton, Stack } from '@fluentui/react';
import { getISOWeek } from 'date-fns';
import { GetMonthNameShort } from '../../../../components/Utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiCalls } from '../../../../api/api';
import { Period, PeriodState, SetPeriodStateDto } from '../../../../api/generated/data-contracts';
import { ALL_PERIODS } from '../../../../constants/queryKeys';

const dropdownControlledExampleOptions = [
    { key: 'Open', text: 'Open' },
    { key: 'Pending', text: 'Pending' },
    { key: 'Closed', text: 'Closed' },
    { key: 'All', text: 'All' },
];

interface IProps {
    allPeriods: Period[];
    isLoading: boolean;
}

const defaultPeriodSpan = 6;
export const PeriodConfigOverview: React.FC<IProps> = props => {
    const [selectedItemStateFilter, setSelectedItemStateFilter] = useState<IDropdownOption>();
    const [filteredPeriods, setFilteredPeriods] = useState<Period[]>([]);
    const [errors, setErrors] = useState('');

    const onChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setSelectedItemStateFilter(item);
    };

    const addPeriod = () => {
        let newStartDate = new Date();

        if (filteredPeriods.length > 0) {
            const newestPeriod = filteredPeriods.reduce((a, b) => (new Date(a.endDate) > new Date(b.endDate) ? a : b));
            newStartDate = new Date(newestPeriod.endDate);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else {
            newStartDate.setUTCDate(newStartDate.getDate() - ((newStartDate.getDay() + 6) % 7));
        }

        const newEndDate = new Date(newStartDate);
        newEndDate.setDate(newEndDate.getDate() + defaultPeriodSpan);
        const startDateWeek = getISOWeek(newStartDate).toString();
        const endDateWeek = getISOWeek(newEndDate).toString();
        const isSameWeek = startDateWeek === endDateWeek;

        let name = `${newStartDate.getDate()} ${GetMonthNameShort(newStartDate.getMonth())} - ${newEndDate.getDate()} ${GetMonthNameShort(
            newEndDate.getMonth(),
        )} (W ${isSameWeek ? startDateWeek : `${startDateWeek} - ${endDateWeek}`})`;

        //Maybe we should refactor and create the period instant? then have a activation flag or something
        const existingName = filteredPeriods.find(_ => _.name === name);
        if (existingName) {
            name += '_2';
        }

        // const newPeriod = new Period('', name, newStartDate, newEndDate, 0, null);
        const newPeriod: Period = {
            name,
            startDate: newStartDate.getISOStringMidnight(),
            endDate: newEndDate.getISOStringMidnight(),
            state: 'Open',
        };

        setFilteredPeriods(fp => [...fp, newPeriod]);
    };

    const { mutateAsync } = useMutation((period: Period) => ApiCalls.editPeriod(period));

    const handleChangeName = async (period: Period, name: string) => {
        await mutateAsync(period);
        setFilteredPeriods(fp => {
            return fp.map(p => {
                if (period.name !== p.name) {
                    return p;
                }
                p.name = name;
                return p;
            });
        });
    };

    const handleStartDateChange = (period: Period, date: Date) => {
        let newEndDate: Date = null;
        if (date.getTime() > new Date(period.endDate).getTime()) {
            newEndDate = new Date(date);
            newEndDate.setDate(date.getDate() + defaultPeriodSpan);
        }
        const endDate = new Date(newEndDate || period.endDate);
        const startDateWeek = getISOWeek(date).toString();
        const endDateWeek = getISOWeek(endDate).toString();
        const isSameWeek = startDateWeek === endDateWeek;

        const newName = `${date.getDate()} ${GetMonthNameShort(date.getMonth())} - ${endDate.getDate()} ${GetMonthNameShort(endDate.getMonth())} (W ${
            isSameWeek ? startDateWeek : `${startDateWeek} - ${endDateWeek}`
        })`;

        setFilteredPeriods(fp => {
            return fp.map(p => {
                if (period.name != p.name) {
                    return p;
                }
                p.name = newName;
                p.startDate = date.getISOStringMidnight();
                if (newEndDate) {
                    p.endDate = newEndDate.getISOStringMidnight();
                }
                return p;
            });
        });
    };

    const handleEndDateChange = (period: Period, date: Date) => {
        let newStartDate: Date = null;
        if (new Date(period.startDate).getTime() > date.getTime()) {
            newStartDate = new Date(date);
            newStartDate.setDate(date.getDate() - defaultPeriodSpan);
        }

        const periodStartDate = new Date(period.startDate);
        const startDateWeek = getISOWeek(newStartDate || periodStartDate).toString();
        const endDateWeek = getISOWeek(date).toString();
        const isSameWeek = startDateWeek === endDateWeek;
        const startDate = new Date(newStartDate || period.startDate);
        const newName = `${startDate.getDate()} ${GetMonthNameShort(startDate.getMonth())} - ${date.getDate()} ${GetMonthNameShort(date.getMonth())} (W ${
            isSameWeek ? startDateWeek : `${startDateWeek} - ${endDateWeek}`
        })`;

        setFilteredPeriods(fp => {
            return fp.map(p => {
                if (period.name !== p.name) {
                    return p;
                }

                p.name = newName;
                p.endDate = date.getISOStringMidnight();
                if (newStartDate) {
                    p.startDate = newStartDate.getISOStringMidnight();
                }
                return p;
            });
        });
    };

    const { mutate: mutatePeriodState } = useMutation((dto: SetPeriodStateDto) => ApiCalls.setPeriodState(dto));

    const handlePeriodChange = (period: Period, optionKey: PeriodState) => {
        mutatePeriodState({
            periodId: period.id,
            state: optionKey,
        });

        setFilteredPeriods(fp => {
            return fp.map(p => {
                if (period.id === p.id) {
                    return { ...p, state: optionKey };
                }
                return p;
            });
        });
    };

    const deletePeriod = async (period: Period) => {
        if (period.id) {
            try {
                await ApiCalls.deletePeriod(period);
            } catch (error) {
                console.debug('Could not delete period', period);
                return;
            }
        } else {
            setFilteredPeriods(prev => [...prev.filter(p => !(p.id === period.id && p.name === period.name))]);
            console.debug('Period deleted', period);
            return;
        }

        setFilteredPeriods(prev => [...prev.filter(p => p.id !== period.id)]);
        console.debug('Period deleted', period);
    };

    const queryClient = useQueryClient();

    const savePeriod = (period: Period) => {
        ApiCalls.createPeriod({ endDate: period.endDate, startDate: period.startDate, name: period.name, state: period.state }).then(
            ({ data: createdPeriod }) => {
                // periods needs to be fetched from new when entering timesheet again
                queryClient.invalidateQueries({ queryKey: [ALL_PERIODS], refetchType: 'none' });
                if (!createdPeriod) {
                    setErrors(e => e + `${period.name} Period is overlapping!`);
                    return;
                }
                setFilteredPeriods(fp => {
                    return fp.map(p => {
                        if (
                            new Date(p.startDate).getTime() !== new Date(period.startDate).getTime() &&
                            new Date(p.endDate).getTime() !== new Date(period.endDate).getTime()
                        ) {
                            return p;
                        }
                        p.id = createdPeriod.id;
                        return p;
                    });
                });
            },
        );
    };

    useEffect(() => {
        if (selectedItemStateFilter === undefined) {
            setSelectedItemStateFilter(dropdownControlledExampleOptions[0]);
        }
    }, []);

    useEffect(() => {
        if (selectedItemStateFilter !== undefined && props.allPeriods !== undefined) {
            if (selectedItemStateFilter.key !== 'All') {
                setFilteredPeriods(
                    props.allPeriods.toSorted((p1, p2) => (p1.startDate < p2.startDate ? 1 : -1)).filter(p => p.state === selectedItemStateFilter.key),
                );
            } else {
                setFilteredPeriods(props.allPeriods.toSorted((p1, p2) => (p1.startDate < p2.startDate ? 1 : -1)));
            }
        }
    }, [selectedItemStateFilter, props.allPeriods]);

    return (
        <>
            {errors && <h1>Errors: {errors}</h1>}
            <Stack horizontal wrap tokens={{ childrenGap: 16 }} styles={{ root: { width: '100%', maxWidth: 900, margin: '0 auto', paddingTop: 10 } }}>
                <Stack verticalAlign="center" horizontal>
                    <PrimaryButton text="Add Period" onClick={addPeriod} disabled={props.isLoading} />
                </Stack>
                <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 16 }} wrap>
                    <Dropdown
                        selectedKey={selectedItemStateFilter ? selectedItemStateFilter.key : undefined}
                        onChange={onChange}
                        placeholder="Select an option"
                        options={dropdownControlledExampleOptions}
                        styles={{ dropdown: { width: 150 } }}
                        disabled={props.isLoading}
                    />
                </Stack>
            </Stack>

            <PeriodListPicker
                allPeriods={filteredPeriods}
                savePeriod={savePeriod}
                changePeriodState={handlePeriodChange}
                changeStartDate={handleStartDateChange}
                changeEndDate={handleEndDateChange}
                changeName={handleChangeName}
                deletePeriod={deletePeriod}
            />
        </>
    );
};
